/**
 * MIPerformanceProfiler — FPS / draw-call / GPU memory profiler with adaptive
 * LOD + interaction-aware quality reduction (closes IGIST gap #10).
 *
 * Provides a small HUD overlay and emits frame-stat events. The profiler is
 * non-invasive: if the scene does not expose hooks it falls back to RAF-based
 * FPS measurement only.
 */

import type { MISceneView } from '../core/MISceneView';

export interface MIPerformanceProfilerOptions {
  fpsTarget?: number;
  adaptiveLOD?: boolean;
  reducedShadowsDuringInput?: boolean;
  postFxOffDuringInput?: string[];
  hudOverlay?: boolean;
  sampleWindowMs?: number;
}

export interface MIFrameStats {
  fps: number;
  frameTimeMs: number;
  drawCalls?: number;
  gpuMemoryMB?: number;
  triangles?: number;
  resolutionScale?: number;
  interacting: boolean;
}

export class MIPerformanceProfiler {
  private scene: MISceneView;
  private opts: Required<MIPerformanceProfilerOptions>;
  private rafId: number | null = null;
  private lastFrameTime = 0;
  private frameCount = 0;
  private windowStart = 0;
  private currentFps = 60;
  private interacting = false;
  private hud: HTMLDivElement | null = null;
  private listeners = new Map<string, Set<(...a: any[]) => void>>();
  private interactTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(scene: MISceneView, options: MIPerformanceProfilerOptions = {}) {
    this.scene = scene;
    this.opts = {
      fpsTarget: options.fpsTarget ?? 60,
      adaptiveLOD: options.adaptiveLOD ?? true,
      reducedShadowsDuringInput: options.reducedShadowsDuringInput ?? true,
      postFxOffDuringInput: options.postFxOffDuringInput ?? ['ssao', 'bloom'],
      hudOverlay: options.hudOverlay ?? false,
      sampleWindowMs: options.sampleWindowMs ?? 1000,
    };
    if (this.opts.hudOverlay) this.installHud();
    this.attachInteractionTracking();
    this.start();
  }

  on(event: 'frame' | 'fpsdrop' | 'recover', h: (s: MIFrameStats) => void): void {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event)!.add(h);
  }
  off(event: string, h: (...a: any[]) => void): void {
    this.listeners.get(event)?.delete(h);
  }
  private emit(event: string, stats: MIFrameStats): void {
    this.listeners.get(event)?.forEach((h) => {
      try {
        h(stats);
      } catch {
        /* ignore */
      }
    });
  }

  /** Snapshot the most recent frame statistics. */
  getStats(): MIFrameStats {
    return {
      fps: this.currentFps,
      frameTimeMs: this.currentFps > 0 ? 1000 / this.currentFps : 0,
      interacting: this.interacting,
    };
  }

  start(): void {
    if (this.rafId !== null) return;
    this.windowStart = performance.now();
    this.frameCount = 0;
    this.lastFrameTime = performance.now();
    this.rafId = requestAnimationFrame(this.tick);
  }

  stop(): void {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }

  dispose(): void {
    this.stop();
    if (this.hud?.parentNode) this.hud.parentNode.removeChild(this.hud);
    this.hud = null;
    if (this.interactTimer) clearTimeout(this.interactTimer);
    this.listeners.clear();
  }

  private tick = (): void => {
    const now = performance.now();
    this.frameCount++;
    const elapsed = now - this.windowStart;
    if (elapsed >= this.opts.sampleWindowMs) {
      const newFps = (this.frameCount * 1000) / elapsed;
      const dropped = this.currentFps - newFps > 10 && newFps < this.opts.fpsTarget * 0.8;
      const recovered = this.currentFps < this.opts.fpsTarget * 0.8 && newFps >= this.opts.fpsTarget * 0.95;
      this.currentFps = newFps;
      this.frameCount = 0;
      this.windowStart = now;
      const stats = this.getStats();
      this.emit('frame', stats);
      if (dropped) this.emit('fpsdrop', stats);
      if (recovered) this.emit('recover', stats);
      if (this.opts.adaptiveLOD) this.applyAdaptiveLOD(stats);
      if (this.hud) this.updateHud(stats);
    }
    this.lastFrameTime = now;
    this.rafId = requestAnimationFrame(this.tick);
  };

  private applyAdaptiveLOD(stats: MIFrameStats): void {
    const s = this.scene as any;
    const ratio = stats.fps / this.opts.fpsTarget;
    if (typeof s.setMaximumScreenSpaceError === 'function') {
      const sse = ratio < 0.7 ? 24 : ratio < 0.9 ? 16 : 8;
      try { s.setMaximumScreenSpaceError(sse); } catch { /* ignore */ }
    }
    if (this.interacting && this.opts.reducedShadowsDuringInput) {
      try { s.setShadowsEnabled?.(false); } catch { /* ignore */ }
    } else {
      try { s.setShadowsEnabled?.(true); } catch { /* ignore */ }
    }
  }

  private attachInteractionTracking(): void {
    const s = this.scene as any;
    const onInteract = () => {
      this.interacting = true;
      if (this.interactTimer) clearTimeout(this.interactTimer);
      this.interactTimer = setTimeout(() => (this.interacting = false), 250);
    };
    if (typeof s.on === 'function') {
      try { s.on('move', onInteract); s.on('cameraChanged', onInteract); } catch { /* ignore */ }
    }
  }

  private installHud(): void {
    this.hud = document.createElement('div');
    this.hud.className = 'migis-perf-hud';
    Object.assign(this.hud.style, {
      position: 'absolute',
      top: '8px',
      right: '8px',
      padding: '6px 10px',
      font: '12px/1.4 ui-monospace, Menlo, Consolas, monospace',
      background: 'rgba(0,0,0,0.7)',
      color: '#9fe',
      borderRadius: '4px',
      zIndex: '9999',
      pointerEvents: 'none',
    });
    this.hud.textContent = 'FPS — measuring…';
    const container = (this.scene as any).container ?? document.body;
    try { container.appendChild(this.hud); } catch { document.body.appendChild(this.hud); }
  }

  private updateHud(s: MIFrameStats): void {
    if (!this.hud) return;
    const bad = s.fps < this.opts.fpsTarget * 0.7;
    this.hud.style.color = bad ? '#fda4af' : '#9fe';
    this.hud.textContent = `FPS ${s.fps.toFixed(0)} · ${s.frameTimeMs.toFixed(1)}ms${s.interacting ? ' · INPUT' : ''}`;
  }
}
